import { qfRestGet } from './qfClient';
import type { SortMode } from '../components/TickerDetail/TopContractsPanel';
import type { OptionType, TopContractRow, TopContractsBoard } from '../components/TickerDetail/topContracts';

/**
 * `GET /v1/steamroom/tickers/{symbol}/top-contracts` — the session's top
 * contracts for one underlying, split calls / puts, ranked by directional
 * net premium.
 *
 * **LIVE.** Steamroom-namespaced, so it takes the same REST bearer as
 * `/v1/steamroom/alerts/history` and carries the same entitlement floor: a
 * valid signature alone gets 403 without an alerts permission (`steamroom`,
 * `Alpha`, `jdash_access` or `wsjonly`).
 *
 * Contract settled with QuantForge 2026-09-01 (their PR #1186) — see
 * `docs/plans/2026-09-01-top-contracts-net-premium-contract.md`.
 *
 * ### ⚠️ This is a TAPE board, not an alert board
 *
 * The candidate set is the full option tape: **every contract that traded is
 * eligible**, ranked by the money on it. `orders` is an alert count joined on
 * as an ANNOTATION and is 0 on most rows.
 *
 * ⚠️ This file previously documented the opposite — that only alerting
 * contracts were eligible and a heavily traded contract's absence was "by
 * design". That stopped being true at QF's #975, which changed the candidate
 * set from the alert store to the tape precisely because the alert floor
 * admitted only a handful of contracts per day market-wide and could not
 * populate a per-ticker board. That is the likely cause of the 2026-08-13 MU
 * empty-panel report.
 *
 * ### Ranked server-side, per side
 *
 * `calls` and `puts` are ranked independently. Nothing downstream re-sorts:
 * re-ranking here would reorder only the rows that survived the server's cut.
 */

/** ⚠️ The wire shape, exactly as served. `snake_case`. */
interface TopContractWire {
  /** `YYYY-MM-DD`. */
  expiry: string;
  strike: number;
  /**
   * ⚠️ **`"C"` or `"P"` — NOT `"Call"`/`"Put"`.**
   *
   * `/v1/contract-leaders` spells these out; this route abbreviates. Normalised
   * in `toRow` below so exactly one place knows about the abbreviation — a raw
   * `"C"` reaching the panel would fail its comparison silently and render
   * every call row as a put.
   */
  option_type: 'C' | 'P';

  /** ⚠️ Alert count, not tape orders. 0 on most rows. */
  orders: number;
  /** ⚠️ GROSS premium, unsigned. Not directional — see `net_premium`. */
  premium: number;

  /**
   * ⚠️ **Signed, directional.** Positive is net bullish.
   *
   * `(call_ask + put_bid) − (call_bid + put_ask)`. Do NOT confuse with the
   * aggression lookalike `(call_ask + put_ask) − (call_bid + put_bid)`, which
   * scores bearish put buying as positive.
   */
  net_premium: number;

  /** Share of DIRECTIONAL premium (ask+bid) lifted at the offer, 0–100. */
  ask_premium_pct: number;

  /**
   * Share of TOTAL premium (ask+bid+mid+no_side) that never picked a side,
   * 0–100.
   *
   * ⚠️ Does NOT sum to 100 with `ask_premium_pct` — different denominators,
   * by design.
   */
  mid_premium_pct: number;

  volume: number;
  open_interest: number;
}

/** ⚠️ The wire shape. */
interface TopContractsWire {
  session_date: string;
  symbol: string;
  calls: TopContractWire[];
  puts: TopContractWire[];
  /** ⚠️ A LIVE TICK, not the session's close. */
  spot: number | null;
  /** ISO-8601 UTC timestamp of `spot`. */
  spot_as_of: string | null;
}

export interface TopContractsParams {
  /**
   * ET session, `YYYY-MM-DD`.
   *
   * ⚠️ **Do not send this.** `spot` is a live tick, so a dated board would
   * measure moneyness against today's price with no error — QF has not built
   * session-aware spot, on our explicit say-so that these panels never request
   * a historical board. If that changes, session-aware spot is a prerequisite,
   * not an improvement.
   */
  date?: string;
  /** Rows per side, 1–50. Server defaults to 10; outside the range is a 422. */
  limit?: number;
  /**
   * Ranking. `signed` (default) ranks each side by its own direction; `abs`
   * ranks by magnitude and can put a net-SOLD contract at the top of its panel.
   *
   * ⚠️ If you pass `abs`, the panel MUST say so — pass the same value to
   * `TopContractsPanel`'s `sortMode`.
   */
  sort?: SortMode;
}

/**
 * Thrown when the response's shape is not what the contract promises.
 *
 * ⚠️ **This exists because of the 2026-08-13 MU report.** The old mapper did
 * `(res.calls ?? []).map(...)`, so a response keying its arrays anything other
 * than `calls`/`puts` — `top_calls`, `call_contracts` — became an empty board
 * with no error anywhere, indistinguishable in the UI from a quiet session.
 * QF's published field names have been wrong on this namespace before. A shape
 * mismatch must be loud.
 */
export class TopContractsShapeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TopContractsShapeError';
  }
}

/** A finite number, or null — never a silent 0. */
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/**
 * Normalises one wire row.
 *
 * ⚠️ Numeric fields that are absent or non-numeric come through as `null`
 * rather than `0`. A missing `net_premium` coerced to 0 would render a
 * plausible flat board and read as a quiet contract rather than a mapping bug.
 */
function toRow(w: TopContractWire): TopContractRow | null {
  const netPremium = num(w.net_premium);
  const strike = num(w.strike);
  if (netPremium == null || strike == null || !w.expiry) return null;

  const optionType: OptionType = w.option_type === 'C' ? 'Call' : 'Put';

  return {
    strike,
    optionType,
    expiry: w.expiry,
    netPremium,
    askPct: num(w.ask_premium_pct) ?? 0,
    midPct: num(w.mid_premium_pct) ?? 0,
    volume: num(w.volume) ?? 0,
    openInterest: num(w.open_interest) ?? 0,
  };
}

/**
 * Maps one side, and reports how many rows the mapper could not use.
 *
 * A row dropped here is a row the server sent and we could not read — which is
 * a mapping fault, not an empty session. Counted so the caller can tell the
 * difference.
 */
function toSide(raw: unknown, side: string): { rows: TopContractRow[]; dropped: number } {
  if (!Array.isArray(raw)) {
    throw new TopContractsShapeError(
      `top-contracts: '${side}' is ${raw === undefined ? 'absent' : typeof raw}, expected an array`
    );
  }
  const rows: TopContractRow[] = [];
  let dropped = 0;
  for (const w of raw as TopContractWire[]) {
    const row = toRow(w);
    if (row) rows.push(row);
    else dropped += 1;
  }
  return { rows, dropped };
}

/**
 * Both ranked sides for one symbol, plus the spot the moneyness is measured
 * against.
 *
 * ⚠️ **Index weeklies: pass the root the reader chose.** `SPXW`/`NDXP` are
 * expanded into their families server-side; mapping `SPXW → SPX` here would
 * swap the weeklies for the monthlies under the same heading.
 *
 * The symbol is path-encoded — dotted roots like `BRK.B` must survive intact.
 *
 * @throws {TopContractsShapeError} when `calls`/`puts` are not arrays, which
 *   means the wire contract changed rather than that the session was quiet.
 */
export async function fetchTopContracts(
  symbol: string,
  params: TopContractsParams = {}
): Promise<TopContractsBoard> {
  const query: Record<string, string> = {};
  // Only params the caller set are sent, so the server's defaults stay in force.
  if (params.limit != null) query.limit = String(params.limit);
  if (params.date) query.date = params.date;
  if (params.sort) query.sort = params.sort;

  const path = `/v1/steamroom/tickers/${encodeURIComponent(
    symbol.trim().toUpperCase()
  )}/top-contracts`;
  const res = await qfRestGet<TopContractsWire>(path, query);

  const calls = toSide(res?.calls, 'calls');
  const puts = toSide(res?.puts, 'puts');

  const dropped = calls.dropped + puts.dropped;
  if (dropped > 0) {
    // Loud on purpose: rows the server sent and we could not read mean the
    // wire changed under us. Not thrown — a partial board still beats none —
    // but it must not pass silently the way the MU case did.
    console.warn(
      `[top-contracts] ${symbol}: dropped ${dropped} unreadable row(s); check net_premium/strike/expiry field names`
    );
  }

  return {
    calls: calls.rows,
    puts: puts.rows,
    spot: num(res?.spot),
    spotAsOf: typeof res?.spot_as_of === 'string' ? res.spot_as_of : null,
    sessionDate: res?.session_date ?? '',
  };
}

/**
 * True when no session could be resolved at all.
 *
 * ⚠️ **Not the same as a quiet ticker.** A resolved `sessionDate` with both
 * sides empty means the session exists and this underlying had no contracts —
 * an ordinary outcome. Only an empty `sessionDate` is the unavailable state.
 * Rendering both the same way tells a reader the feed is broken when it works.
 */
export function isTopContractsUnavailable(
  data: TopContractsBoard | null | undefined
): boolean {
  if (!data) return false;
  return !data.sessionDate;
}
